// Arcade skate physics. Deliberately not a rigid-body sim: THPS-style games feel
// right because the board sticks to the surface, gravity is heavy, and the rules
// for landing are forgiving but binary. Everything here is plain math so it can
// be unit tested without a renderer.
//
// `Skater` holds the state and runs `step`; each mode's step lives in `sim/`
// (ground, manual, air, landing, wall, grind, lip, bail) as plain functions
// over the skater. Tuning constants are in `sim/tuning.ts`.

import { NO_CHEATS, type Cheats } from "./cheats";
import { ComboLog } from "./combos";
import { gapsHit, type Gap } from "./gaps";
import type { InputFrame } from "./input";
import { outOfBounds, surfaceAt, type Level, type Surface } from "./level";
import { stepAir } from "./sim/air";
import { stepBail } from "./sim/bail";
import { newPack, stepPack, type PackState } from "./sim/backpack";
import { placeProps } from "./props";
import { canStepOff, readChord, stepFoot, stepOff, undoChord } from "./sim/foot";
import { stepShore } from "./sim/beach";
import { stepRideOut } from "./sim/ride-out";
import { stepGrind } from "./sim/grind";
import { stepGround } from "./sim/ground";
import { newHill, stepHill } from "./sim/hill";
import { scoreTricks } from "./hill-score";
import { placeRoad } from "./hill-ramps";
import { stepLip } from "./sim/lip";
import { readManualInput } from "./sim/manual";
import { bodyFlipFor, RIDES, type BodyFlip, type RideId, type RideTricks } from "./sim/rides";
import { stepVehicleHits } from "./sim/skitch";
import type {
  AirState,
  ChordState,
  FlipState,
  FootState,
  GrabState,
  GrindState,
  HillState,
  Landing,
  LipState,
  ManualState,
  Mode,
  SkaterEvent,
  SkitchState,
  WallrideState,
} from "./sim/state";
import {
  GRIND_BUFFER,
  HOLD_WOBBLE,
  LIP_WOBBLE,
  PLANT_BUFFER,
  PLANT_STICK,
  REVERT_BUFFER,
  SLOPPY_KEEP,
  PERFECT_BONUS,
  SWITCH_BALANCE,
  BALANCE_STICK,
  BALANCE_STICK_BRAKE,
  SWITCH_BONUS,
} from "./sim/tuning";
import { SpecialMeter } from "./special";
import { DEFAULT_STATS, statScale, type StatId, type Stats } from "./stats";
import { Combo, DEFAULT_SPECIAL_SEQS, specialTrick, stickDir, StickHistory, FlipAim, type Dir, type PopVariant, type PopVariantId, type SpecialSeqs, type SpecialTrick } from "./tricks";

export { ollieLift } from "./sim/ground";
export type { FootState, GrindState, Landing, LipState, ManualState, Mode, SkaterEvent, SkitchState, WallrideState } from "./sim/state";
export {
  AIR_SPIN_RATE,
  BAIL_TIME,
  G,
  OLLIE_V,
  PERFECT_ANGLE,
  PLANT_STICK,
  REVERT_WINDOW,
  SLOPPY_ANGLE,
  SPECIAL_RECOVER,
  WALLRIDE_TIME,
} from "./sim/tuning";

export class Skater {
  mode: Mode = "ground";
  x = 0;
  y = 0;
  z = 0;
  /** Air (and bail slide) velocity. */
  vx = 0;
  vy = 0;
  vz = 0;
  /** Ground speed along the board's nose direction. Negative means rolling fakie. */
  speed = 0;
  /** Board heading. Nose points along (sin yaw, cos yaw) in (x, z). */
  yaw = 0;
  /** Surface normal under the board (world up while airborne). */
  normal = { x: 0, y: 1, z: 0 };
  crouch = 0;
  charge = 0;
  /** Everything about the current air (flip, grab, spin, vert, wall...); null unless airborne. */
  air: AirState | null = null;
  grind: GrindState | null = null;
  lip: LipState | null = null;
  /** Set while manualing; the skater is still in "ground" mode. */
  manual: ManualState | null = null;
  /** Set while towed behind a vehicle (#37); still "ground" mode, like a manual. */
  skitch: SkitchState | null = null;
  /** Set while off the board and on foot (#19), "foot" mode. */
  foot: FootState | null = null;
  /** The backpack (#136): the item out on foot and the paintballs in flight. */
  pack: PackState = newPack();
  /** What you're riding (#141): the board, or a ride from the pack. Kept through a respawn. */
  ride: RideId = "board";
  /**
   * Sim time since this skater was made (s). The map's vehicles drive their
   * loops by it (`vehiclePose`), so a respawn doesn't move them.
   */
  clock = 0;
  bailT = 0;
  /** Bailing into the sea (#82): when the bail's over you go back to `lastSafe`. */
  wipeout = false;
  /** The last spot on flat concrete floor (where a Wipeout puts you back). */
  lastSafe = { x: 0, z: 0, yaw: 0 };
  /** The map has a ride's track out past the fence (#189), so coming off out there is a bail. */
  readonly rideOut: boolean;
  combo = new Combo();
  /** Fills as combos land, empties on a bail; the fuller it is the faster and higher you go. */
  special = new SpecialMeter();
  /** Air, speed, spin, balance... (THPS): the default all-rounder rides today's tuning. Game hands in the session's. */
  stats: Readonly<Stats> = DEFAULT_STATS;
  /** Cheats on (#119): Game hands in the settings' cheats; demo skaters ride without. */
  cheats: Cheats = NO_CHEATS;
  /** Each special's two taps (#135): Game hands in the settings'; demo skaters ride the defaults. */
  specials: SpecialSeqs = DEFAULT_SPECIAL_SEQS;
  score = 0;
  /** Every combo landed this session. */
  combos = new ComboLog();
  /** L2 toggles this on the ground; the next ollie pops off the nose. */
  nollie = false;
  /** R2 on flat ground: riding with the other foot forward. */
  switchStance = false;
  /** Seconds since the last revert, for the pivot animation. */
  pivotT = 1;

  // Sim internals below: read and written by the mode modules in sim/, not
  // meant for the game or the renderer.

  /** The landing that opened the current ground stretch, until the combo banks or you leave the ground. */
  landing: Landing | null = null;
  /** The last up, up or down, down, and how long letting go of ollie can still pop it. */
  popSeq: [Dir, Dir] | null = null;
  popSeqT = 0;
  /**
   * Ollie let go of while riding up the vert face, waiting for the launch
   * line: the crouch charge, and how it was popped. Dropped if you stop
   * climbing or leave the ramp first.
   */
  vertOllie: { charge: number; variant: PopVariant | null } | null = null;
  /** Grind balance carries through a combo (THPS): where the needle was left... */
  balanceCarry: number | null = null;
  /** ...and the manual needle too (its own up/down meter). */
  manualCarry: number | null = null;
  /** ...and how long you've been balancing this combo, which sets the difficulty. */
  balanceTime = 0;
  revertWindow = 0;
  /** Counts down after the revert window closes: R2 in it is a late revert, not a stance switch (#134). */
  lateRevert = 0;
  /** Counts down after R2 switches stance on the ground: taking off in it undoes the switch (#134). */
  stanceTap = 0;
  /** Seconds the combo stays open after a flat landing or a revert, waiting for a manual to link it (#69). */
  linkWindow = 0;
  revertBuffer = 0;
  plantBuffer = 0;
  /** Same as plantBuffer, for the grind button (Y / Triangle), which only plants head-on. */
  plantBufferGrind = 0;
  /** Events raised by helpers that don't take the events list (e.g. banking on leaving the ground). */
  out: SkaterEvent[] = [];
  grindBuffer = 0;
  /** Grind held this step, for helpers that don't take the input (a wall hit starts a wallride). */
  grindHeld = false;
  /** Where the stick was when grind was last pressed: a lip trick reached on the buffer uses it. */
  grindPressDir: Dir = "n";
  grindCooldown = 0;
  sticks = new StickHistory();
  /** The stick's last few readings, for which flip a press picks (#85). */
  flipAim = new FlipAim();
  /** A manual sequence waiting to start: seconds left, and which one. */
  manualBuffer = 0;
  manualNose = false;
  /** The last two-press stick sequence, and how long it can still pull a special. */
  specialSeq: [Dir, Dir] | null = null;
  specialSeqT = 0;
  /** Where the current manual started (manual gaps). */
  manualFrom: { x: number; z: number } | null = null;
  /** Manual gaps already scored in this manual. */
  manualGaps = new Set<Gap>();
  /** The vehicle just let go of, which can't knock you down for `t` more seconds while you pull past it. */
  skitchGrace: { vehicle: number; t: number } | null = null;
  /** Bomb the Hill (#193): the run down the hill, on a hill map; null anywhere else. */
  hill: HillState | null = null;
  /** The step-off chord's first trigger, waiting for the second (#19, `sim/foot.ts`). */
  chord: ChordState | null = null;

  constructor(
    readonly level: Level,
    readonly rng: () => number = Math.random,
  ) {
    this.rideOut = level.rails.some((r) => r.track);
    this.pack.props = placeProps(level);
    this.respawn();
  }

  /** Points of the best combo landed this session. */
  get bestCombo(): number {
    return this.combos.best?.points ?? 0;
  }

  get forward(): { x: number; z: number } {
    return { x: Math.sin(this.yaw), z: Math.cos(this.yaw) };
  }

  /** Horizontal speed, whatever the mode — for the camera and animation. */
  get horizontalSpeed(): number {
    if (this.mode === "ground") return Math.abs(this.speed);
    if (this.mode === "grind" && this.grind) return this.grind.speed;
    if (this.mode === "foot" && this.foot) return Math.abs(this.foot.speed);
    return Math.hypot(this.vx, this.vz);
  }

  /**
   * Which way the skater is moving relative to the board's nose: +1 nose-first,
   * -1 fakie, 0 when there's no clear direction (nearly stopped, or straight
   * up and down on vert). The model uses it to face the rider where they're going.
   */
  get travelSign(): -1 | 0 | 1 {
    const f = this.forward;
    let v = 0;
    if (this.mode === "ground") {
      v = this.speed;
    } else if (this.mode === "foot" && this.foot) {
      v = this.foot.speed;
    } else if (this.mode === "grind" && this.grind) {
      const g = this.grind;
      const len = Math.hypot(g.rail.b.x - g.rail.a.x, g.rail.b.z - g.rail.a.z);
      v = (((g.rail.b.x - g.rail.a.x) * f.x + (g.rail.b.z - g.rail.a.z) * f.z) / len) * g.dir * g.speed;
    } else {
      v = this.vx * f.x + this.vz * f.z;
    }
    if (Math.abs(v) < 0.5) return 0;
    return v > 0 ? 1 : -1;
  }

  /** How unstable the balance needle is right now; builds up over a combo's grinds. */
  get balanceDifficulty(): number {
    return (1 + this.balanceTime * 0.2) * (this.switchStance ? this.switchPenalty(SWITCH_BALANCE) : 1);
  }

  /** What a stat multiplies (see `statScale`): exactly 1 at the default. */
  scale(id: StatId): number {
    const s = statScale(this.stats, id);
    const ride = RIDES[this.ride].scales[id];
    return ride === undefined ? s : s * ride;
  }

  /** The ride's trick tables (#141): the THPS ones on the board. */
  get tricks(): RideTricks {
    return RIDES[this.ride].tricks;
  }

  /** A switch penalty (a factor over 1), softened or sharpened by the switch stat. */
  switchPenalty(factor: number): number {
    // On skates (#140), a bike or a scooter (#171) there's no stance to swap.
    if (this.tricks.stanceFree) return 1;
    return factor ** this.scale("switch");
  }

  /** How hard a balance needle wobbles: the coping and held ground tricks are harder, the balance stats steadier. */
  wobble(kind: "grind" | "lip" | "manual" | "hold"): number {
    if (kind === "grind") return this.scale("rail");
    if (kind === "lip") return LIP_WOBBLE * this.scale("lip");
    return (kind === "hold" ? HOLD_WOBBLE : 1) * this.scale("manual");
  }

  /** This air's flip, grab and so on (null when not airborne). */
  get flip(): FlipState | null {
    return this.air?.flip ?? null;
  }

  get grab(): GrabState | null {
    return this.air?.grab ?? null;
  }

  get wallride(): WallrideState | null {
    return this.air?.wallride ?? null;
  }

  /** The ramp's up axis on a vert air or a lip trick: the camera and landings line up with it. */
  get vertAxis(): { x: number; z: number } | null {
    if (this.air) return this.air.vertAxis;
    return this.lip ? { x: this.lip.axisX, z: this.lip.axisZ } : null;
  }

  /** Accumulated player-driven rotation during the current air. */
  get spin(): number {
    return this.air?.spin ?? 0;
  }

  /** Boneless / Fastplant this air was popped with, for the pose; and seconds since the pop. */
  get popVariant(): PopVariantId | null {
    return this.air?.popVariant ?? null;
  }

  get popT(): number {
    return this.air?.popT ?? 0;
  }

  /** Up against a wall in the air right now (hanging or planted). */
  get wallContact(): boolean {
    return (this.air?.wall ?? null) !== null;
  }

  /** 0..1 through a wallplant's stick on the wall, or null when not planting. For the pose. */
  get plantProgress(): number | null {
    const w = this.air?.wall;
    return w?.planted ? Math.min(1, w.t / PLANT_STICK) : null;
  }

  /** Which way the wall being planted on faces, for the pose. */
  get plantNormal(): { x: number; z: number } | null {
    const w = this.air?.wall;
    return w?.planted ? w.n : null;
  }

  /** Back on the board, standing still at `at` (the map's spawn unless told otherwise), with the combo and special cleared. */
  respawn(at: { x: number; z: number; yaw: number } = this.level.spawn): void {
    const { x, z, yaw } = at;
    this.lastSafe = { x, z, yaw };
    this.wipeout = false;
    this.x = x;
    this.z = z;
    this.y = surfaceAt(this.level, x, z).h;
    this.yaw = yaw;
    this.speed = 0;
    this.vx = this.vy = this.vz = 0;
    this.mode = "ground";
    this.air = null;
    this.grind = null;
    this.lip = null;
    this.combo.reset();
    this.normal = { x: 0, y: 1, z: 0 };
    this.nollie = false;
    this.switchStance = false;
    this.revertWindow = 0;
    this.lateRevert = 0;
    this.stanceTap = 0;
    this.linkWindow = 0;
    this.manual = null;
    this.manualBuffer = 0;
    this.manualFrom = null;
    this.skitch = null;
    this.skitchGrace = null;
    this.foot = null;
    this.pack.balls.length = 0;
    this.chord = null;
    this.specialSeq = null;
    this.popSeq = null;
    this.popSeqT = 0;
    this.vertOllie = null;
    this.sticks.clear();
    this.flipAim.clear();
    this.resetBalance();
    this.special.empty();
    this.hill = this.level.hill ? this.newRun(z) : null;
  }

  /**
   * A fresh run down the hill from `z` (#193): after a run, the road gets
   * the next run's seed (#195, a new road every run); the first keeps the
   * one it was laid out with.
   */
  private newRun(z: number): HillState {
    const road = this.level.hill?.road;
    if (road) {
      if (this.hill) road.nextRun(this.clock);
      else road.reset(road.seed, this.clock);
      placeRoad(this.level);
    }
    return newHill(z, this.ride, road?.seed ?? 0);
  }

  step(input: InputFrame, dt: number): SkaterEvent[] {
    const events: SkaterEvent[] = [];
    this.out = events;
    this.clock += dt;
    this.pivotT += dt;
    this.lateRevert = Math.max(0, this.lateRevert - dt);
    this.stanceTap = Math.max(0, this.stanceTap - dt);
    this.flipAim.update(input.moveX, input.moveY, dt);
    if (this.air) this.air.popT += dt;
    if (input.pressed.revert && (this.mode === "air" || this.mode === "lip")) this.revertBuffer = REVERT_BUFFER;
    else this.revertBuffer = Math.max(0, this.revertBuffer - dt);
    if (input.pressed.ollie && this.mode === "air") this.plantBuffer = PLANT_BUFFER;
    else this.plantBuffer = Math.max(0, this.plantBuffer - dt);
    if (input.pressed.grind && this.mode === "air") this.plantBufferGrind = PLANT_BUFFER;
    else this.plantBufferGrind = Math.max(0, this.plantBufferGrind - dt);
    if (input.pressed.reset) {
      this.respawn();
      events.push({ type: "respawn" });
      return events;
    }
    this.special.update(dt, this.combo.active);
    if (this.cheats.special) this.special.fill();
    this.grindHeld = input.held.grind;
    if (input.pressed.grind) {
      this.grindBuffer = GRIND_BUFFER;
      this.grindPressDir = stickDir(input.moveX, input.moveY);
    } else this.grindBuffer = Math.max(0, this.grindBuffer - dt);
    this.grindCooldown = Math.max(0, this.grindCooldown - dt);
    readManualInput(this, input, dt);
    let chord = readChord(this, input, dt);
    if (chord && canStepOff(this)) {
      undoChord(this, chord);
      stepOff(this);
      // Stepping off used it: it doesn't also get back on.
      chord = null;
    } else if (chord && this.mode === "ground") {
      // Both triggers where you can't step off: a step-off that didn't
      // happen, not a nollie and a stance switch (#134).
      undoChord(this, chord);
      input = withoutTriggers(input);
      chord = null;
    }

    switch (this.mode) {
      case "ground":
        stepGround(this, input, dt);
        break;
      case "air":
        stepAir(this, input, dt, events);
        break;
      case "grind":
        stepGrind(this, input, dt, events);
        break;
      case "lip":
        stepLip(this, input, dt, events);
        break;
      case "bail":
        stepBail(this, dt);
        break;
      case "foot":
        if (this.foot) stepFoot(this, this.foot, input, chord, dt);
        break;
    }
    stepPack(this, input, dt, events);
    if (this.level.edges) stepShore(this, events);
    if (this.rideOut) stepRideOut(this, events);
    if (this.level.vehicles) stepVehicleHits(this, dt);
    if (this.hill) stepHill(this, this.hill, dt, events);
    if (this.manual && this.manualFrom) {
      for (const g of gapsHit(this.gaps, "manual", this.manualFrom, this)) {
        if (this.manualGaps.has(g)) continue;
        this.manualGaps.add(g);
        this.scoreGap(g);
      }
    }
    // Tricks fill the meter as they're scored, so a long combo can go special partway (#184).
    this.special.add(this.combo.feed());
    return events;
  }

  // ------------------------------------------------ shared by the mode modules

  /**
   * Moves a balance needle (grind or manual) one step: it falls away from
   * center, gets nudged at random, and `stick` pushes it back. Returns true
   * when it tips over.
   */
  stepBalance(b: { balance: number; balanceVel: number }, stick: number, dt: number, wobble = 1): boolean {
    if (this.cheats.balance) {
      // Perfect balance (#119): the needle stays dead center.
      b.balance = b.balanceVel = 0;
      return false;
    }
    const difficulty = this.balanceDifficulty * wobble;
    const push = (this.rng() - 0.5) * 3 * difficulty;
    // The stick's pull grows with the difficulty, so a long combo makes the
    // needle livelier but never outruns you (THPS: loose, easy to overcorrect).
    const pull = stick * BALANCE_STICK * Math.max(1, difficulty);
    b.balanceVel += (b.balance * 2.5 * difficulty + push + pull) * dt;
    // Pushing against the way the needle is moving stops it quickly.
    if (stick * b.balanceVel < 0) b.balanceVel *= 1 - Math.min(1, BALANCE_STICK_BRAKE * Math.abs(stick) * dt);
    b.balanceVel *= 1 - Math.min(1, 1.5 * dt);
    b.balance += b.balanceVel * dt;
    return Math.abs(b.balance) >= 1;
  }

  /** The map's gaps (none on a map without any). */
  get gaps(): readonly Gap[] {
    return this.level.gaps ?? [];
  }

  /** A gap goes into the combo like a trick (repeat decay too) and pops a callout. */
  scoreGap(g: Gap): void {
    this.combo.add(g.name, g.points);
    this.out.push({ type: "gap", name: g.name, points: g.points });
  }

  /** Adds a trick to the combo, as "Switch ..." worth a bit more when riding switch. */
  addTrick(name: string, points: number): number {
    if (!this.ridingSwitch) return this.combo.add(name, points);
    return this.combo.add(`Switch ${name}`, points * SWITCH_BONUS);
  }

  /** Riding switch for scoring: on skates (#140), a bike or a scooter (#171) there's no switch, so no bonus either. */
  get ridingSwitch(): boolean {
    return this.switchStance && !this.tricks.stanceFree;
  }

  /**
   * With the meter full, the special a two-press sequence just before this
   * button pulls (see SPECIAL_TRICKS), if any. Using it spends the sequence,
   * so it isn't also read as a manual waiting for touchdown.
   */
  pullSpecial(button: SpecialTrick["button"], seq = this.specialSeq): SpecialTrick | null {
    const special = this.special.special ? specialTrick(seq, button, this.specials, this.tricks.specials) : null;
    if (special) {
      this.specialSeq = null;
      this.manualBuffer = 0;
    }
    return special;
  }

  /**
   * The ride's body flip (#138) a two-press sequence just before flip pulls,
   * meter or not. Like a special, using it spends the sequence.
   */
  pullBodyFlip(): BodyFlip | null {
    const flip = bodyFlipFor(this.tricks, this.specialSeq);
    if (flip) {
      this.specialSeq = null;
      this.manualBuffer = 0;
    }
    return flip;
  }

  /** Lands the combo: scores it (less for a Sloppy landing, a bit more for a Perfect one) and tells the HUD. */
  bankCombo(): void {
    this.revertWindow = 0;
    this.linkWindow = 0;
    this.resetBalance();
    const landing = this.landing;
    this.landing = null;
    if (!this.combo.active) return;
    const description = this.combo.describe();
    const base = Math.round(this.combo.base);
    const multiplier = this.combo.multiplier;
    const fed = this.combo.fed;
    const scale = landing === "sloppy" ? SLOPPY_KEEP : landing === "perfect" ? PERFECT_BONUS : 1;
    const points = Math.round(this.combo.bank() * scale);
    this.score += points;
    // Only what the meter hasn't had yet: a Perfect's bonus (a Sloppy takes nothing back).
    this.special.add(points - fed);
    this.combos.record({ text: description, base, multiplier, points });
    // On the hill (#205) a combo counts toward the run's trick score, weighted by how far down and how fast it landed.
    const h = this.hill;
    const hill = h && h.end === null && !h.picking ? { hill: scoreTricks(h, points, this.horizontalSpeed) } : {};
    this.out.push(
      landing
        ? { type: "landed", points, combo: description, landing, ...hill }
        : { type: "landed", points, combo: description, ...hill },
    );
  }

  /** The combo is over: the next grind starts with a centered, calm needle. */
  resetBalance(): void {
    this.balanceCarry = null;
    this.manualCarry = null;
    this.balanceTime = 0;
  }

  setNormal(s: Surface): void {
    const l = Math.hypot(s.gx, 1, s.gz);
    this.normal = { x: -s.gx / l, y: 1 / l, z: -s.gz / l };
  }

  outOfBounds(x: number, z: number): boolean {
    return outOfBounds(this.level, x, z);
  }
}

/** The HUD's stance read-out ("Switch · Nollie"), or null; no "Switch" on a ride with no stance (#171). */
export function stanceReadout(sk: Pick<Skater, "ridingSwitch" | "nollie">): string | null {
  return [sk.ridingSwitch ? "Switch" : "", sk.nollie ? "Nollie" : ""].filter(Boolean).join(" · ") || null;
}

/** The frame with the trigger presses (nollie, revert) taken out. */
function withoutTriggers(input: InputFrame): InputFrame {
  return { ...input, pressed: { ...input.pressed, nollie: false, revert: false } };
}
